import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import { useConfirm } from "material-ui-confirm";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useDateFnsLocale } from "../../../../../utils/date-locale";
import { StoreType } from "../../../../../utils/types";
import {
	createHoliday,
	deleteHoliday,
	editHoliday,
	getCompanyHolidays,
	getHolidayTypes,
	getLocationHolidays,
} from "../../../../holidays/store/holidays-slice";
import {
	Holiday,
	HolidayFormValues,
	HolidayScope,
} from "../../../../holidays/types";
import {
	dateKeyToLocalDate,
	toDateKey,
} from "../../../../holidays/utils/holiday-dates";

// Company Holidays are managed only by GATAdmin / CompanyAdmin ("Admin")
const COMPANY_HOLIDAY_ROLES = ["GATAdmin", "Admin"];

interface UseHolidaysSectionProps {
	companyId: number;
	companyName: string;
	locationId?: number;
	locationName?: string;
}

interface DialogState {
	open: boolean;
	mode: "add" | "edit";
	initialData: Holiday | null;
}

interface SnackbarState {
	severity: "success" | "error";
	message: string;
}

const NO_ROLES: string[] = [];

const CLOSED_DIALOG: DialogState = { open: false, mode: "add", initialData: null };

const useHolidaysSection = ({
	companyId,
	companyName,
	locationId,
	locationName,
}: UseHolidaysSectionProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const locale = useDateFnsLocale();
	const scope: HolidayScope = locationId ? "location" : "company";

	const [dialog, setDialog] = useState<DialogState>(CLOSED_DIALOG);
	const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);

	const userRoles: string[] = useSelector(
		(state: StoreType) => state.user?.userData?.userRoles ?? NO_ROLES
	);
	const holidayTypes = useSelector(
		(state: StoreType) => state.holidays.holidayTypes.data
	);
	const companyHolidays = useSelector(
		(state: StoreType) => state.holidays.companyHolidays
	);
	const locationHolidays = useSelector((state: StoreType) =>
		locationId ? state.holidays.locationHolidays[locationId] : undefined
	);
	const createStatus = useSelector(
		(state: StoreType) => state.holidays.createHoliday.status
	);
	const editStatus = useSelector(
		(state: StoreType) => state.holidays.editHoliday.status
	);
	const deleteStatus = useSelector(
		(state: StoreType) => state.holidays.deleteHoliday.status
	);

	const canManageCompanyHolidays = userRoles.some((role) =>
		COMPANY_HOLIDAY_ROLES.includes(role)
	);
	// LocationAdmins may add to their Location; only admins add Company-wide
	const canAddHoliday = scope === "location" || canManageCompanyHolidays;

	const holidays =
		scope === "company" ? companyHolidays.data : locationHolidays?.data ?? [];
	const listStatus =
		scope === "company" ? companyHolidays.status : locationHolidays?.status;

	useEffect(() => {
		dispatch(getHolidayTypes());
	}, [dispatch]);

	useEffect(() => {
		if (locationId) dispatch(getLocationHolidays(locationId));
		else if (companyId) dispatch(getCompanyHolidays(companyId));
	}, [dispatch, companyId, locationId]);

	const showError = useCallback(
		(err: unknown) => {
			const message = (err as { message?: string } | undefined)?.message;
			setSnackbar({
				severity: "error",
				message: message || t("common_error-try-again"),
			});
		},
		[t]
	);

	// A company row reached from the location screen is edited via the
	// company route (admins only); the location's own rows via its route.
	const getRowScope = (holiday: Holiday | null): HolidayScope =>
		scope === "location" && holiday?.locationId !== null
			? "location"
			: "company";

	const target = { companyId, locationId };

	const handleOpenAdd = useCallback(() => {
		setDialog({ open: true, mode: "add", initialData: null });
	}, []);

	const handleOpenEdit = useCallback((holiday: Holiday) => {
		setDialog({ open: true, mode: "edit", initialData: holiday });
	}, []);

	const handleCloseDialog = useCallback(() => setDialog(CLOSED_DIALOG), []);

	const handleCloseSnackbar = useCallback(
		(_event?: unknown, reason?: string) => {
			if (reason === "clickaway") return;
			setSnackbar(null);
		},
		[]
	);

	const handleSubmit = (values: HolidayFormValues) => {
		const holiday = { ...values, holidayTypeId: Number(values.holidayTypeId) };
		const isEdit = dialog.mode === "edit" && dialog.initialData;
		const request = isEdit
			? editHoliday({
				id: (dialog.initialData as Holiday).id,
				scope: getRowScope(dialog.initialData),
				target,
				holiday,
			})
			: createHoliday({ scope, target, holiday });
		(dispatch(request) as any)
			.unwrap()
			.then(() => {
				setDialog(CLOSED_DIALOG);
				setSnackbar({
					severity: "success",
					message: isEdit ? t("holidays_updated") : t("holidays_added"),
				});
			})
			.catch(showError);
	};

	const handleRemove = (holiday: Holiday) => {
		const rowScope = getRowScope(holiday);
		const formattedDate = format(
			dateKeyToLocalDate(toDateKey(holiday.date)),
			"MMM d, yyyy",
			{ locale }
		);
		confirm({
			title: t("holidays_remove-title"),
			content: (
				<Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
					<Typography sx={{ fontSize: "14px" }}>
						{t("holidays_remove-body", {
							name: holiday.name,
							date: formattedDate,
						})}
					</Typography>
					<Typography sx={{ fontSize: "14px" }}>
						{rowScope === "company"
							? t("holidays_remove-body-company", { company: companyName })
							: t("holidays_remove-body-location", { location: locationName })}
					</Typography>
				</Box>
			),
			cancellationText: t("common_cancel"),
			// Plain text + button props (see config/confirm-config.ts): a
			// <Button> here would render inside the dialog's own button and
			// throw off its size and alignment.
			confirmationText: t("holidays_remove"),
			confirmationButtonProps: {
				variant: "contained",
				color: "error",
				disableElevation: true,
			},
		})
			.then(() =>
				(dispatch(deleteHoliday({ id: holiday.id, scope: rowScope, target })) as any)
					.unwrap()
					.then(() =>
						setSnackbar({ severity: "success", message: t("holidays_removed") })
					)
					.catch(showError)
			)
			.catch(() => undefined);
	};

	return [
		{
			t,
			scope,
			holidays,
			holidayTypes,
			isLoading: listStatus === "pending" || deleteStatus === "pending",
			canManageCompanyHolidays,
			canAddHoliday,
			dialog,
			isSubmitting: createStatus === "pending" || editStatus === "pending",
			snackbar,
		},
		{
			handleOpenAdd,
			handleOpenEdit,
			handleCloseDialog,
			handleSubmit,
			handleRemove,
			handleCloseSnackbar,
		},
	] as const;
};

export default useHolidaysSection;
